import React, { useState, type ReactNode, useMemo, useCallback } from 'react'
import { ModalContext, type ModalConfig } from './modalContext'
import { Modal } from './Modal'
import { useI18n } from '../i18n/useI18n'

interface ModalProviderProps {
  children: ReactNode
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modal, setModal] = useState<ModalConfig | null>(null)
  const { t } = useI18n()
  const hideModal = () => setModal(null)

  const showModal = useCallback(
    (config: Omit<ModalConfig, 'id'>) => {
      const id = Math.random().toString(36).slice(2, 9)
      const newModal: ModalConfig = {
        ...config,
        id,
        showCancel: config.showCancel ?? false,
        autoClose: config.autoClose ?? false,
        confirmText: config.confirmText || t('dialog.confirm'),
        cancelText: config.cancelText || t('dialog.cancel'),
        autoCloseDelay: config.autoCloseDelay ?? 3000,
        closeOnOverlayClick: config.closeOnOverlayClick ?? true,
      }

      setModal(newModal)

      if (newModal.autoClose && newModal.autoCloseDelay && newModal.autoCloseDelay > 0) {
        setTimeout(() => {
          hideModal()
        }, newModal.autoCloseDelay)
      }
    },
    [hideModal]
  )

  const isVisible = useMemo(() => modal !== null, [modal])

  const contextValue = useMemo(
    () => ({
      modal,
      showModal,
      hideModal,
      isVisible,
    }),
    [modal, showModal, hideModal, isVisible]
  )

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <Modal />
    </ModalContext.Provider>
  )
}
